<script lang="ts">
  import { navigate, setQuery } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import PlanBadge from '../lib/ui/PlanBadge.svelte';
    import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    ApiError,
    type AdminUser,
    type Community,
    type MemberRoleValue,
  } from '../lib/admin';
  import {
    community,
    ensureCommunities,
    optimisticSkoolId,
    refreshCommunities,
    withCommunity,
    onCommunityChange,
  } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchStaff, fetchStaffVoices, openBillingPortal, searchMembers, setMemberRole, startCheckout, syncBilling, type StaffMember, type StaffVoice } from '@api';

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let staff = $state<StaffMember[]>([]);
  let staffLoading = $state(false);
  // The community whose team + voices are on screen, so the community-change effect
  // doesn't re-issue what `init` already has in flight.

  let q = $state('');
  let results = $state<StaffMember[]>([]);
  let searching = $state(false);
  let busy = $state<Set<number>>(new Set());

  // AI Voices: each staff member's generated voice profile.
  let voices = $state<StaffVoice[]>([]);
  let voicesLoading = $state(false);
  let expandedVoice = $state<Set<number>>(new Set());
  const withVoice = $derived(voices.filter((v) => v.profile_text));

  // Per-approach voice facets, in display order.
  type FacetKey = 'general' | 'acknowledge' | 'celebrate' | 'help' | 'correct';
  const FACETS: { key: FacetKey; label: string }[] = [
    { key: 'general', label: 'General' },
    { key: 'acknowledge', label: 'Acknowledge' },
    { key: 'celebrate', label: 'Celebrate' },
    { key: 'help', label: 'Help' },
    { key: 'correct', label: 'Correct' },
  ];
  // Which facet tab is active per member (defaults to 'general').
  let activeFacet = $state<Record<number, FacetKey>>({});

  function facetText(v: StaffVoice, key: FacetKey): string | null {
    if (key === 'general') return v.profile_text;
    return v.profiles ? v.profiles[key] : null;
  }

  function toggleVoice(id: number): void {
    const s = new Set(expandedVoice);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    expandedVoice = s;
  }

  const ROLES: MemberRoleValue[] = ['member', 'moderator', 'admin', 'owner'];
  const LABEL: Record<string, string> = { owner: 'Owner', admin: 'Admin', moderator: 'Moderator', member: 'Member' };
  const roleOf = $derived(new Map(staff.map((m) => [m.member_id, m.role ?? 'moderator'])));

  // --- billing ----------------------------------------------------------------
  // Plan badge rendering lives in the shared <PlanBadge>; here we only need the
  // upgrade eligibility. A missing status (older API) is a grandfathered/legacy
  // skool — never gated.
  const planOf = (c: Community): string => c.plan_status ?? 'legacy';
  const canUpgrade = (c: Community): boolean => planOf(c) === 'trial' || planOf(c) === 'expired';

  let billingBusy = $state<number | null>(null); // skool id whose checkout is starting
  let portalBusy = $state(false);
  let billingError = $state<string | null>(null);
  let billingSuccess = $state(false);
  let billingCancelled = $state(false);

  // Turn a billing failure into friendly copy (503 = Stripe not wired up yet).
  function billingMsg(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.status === 503) return "Billing isn't configured yet.";
    return err instanceof Error ? err.message : fallback;
  }

  // Read the ?billing=success|cancelled that Checkout redirected back with, then
  // strip it from the URL so a refresh doesn't re-show the note.
  function consumeBillingParam(): boolean {
    let p: string | null = null;
    try {
      p = new URLSearchParams(window.location.search).get('billing');
    } catch {
      p = null;
    }
    if (p === 'success') billingSuccess = true;
    else if (p === 'cancelled') billingCancelled = true;
    if (p) setQuery({ billing: null });
    return p === 'success';
  }

  async function upgrade(skoolId: number): Promise<void> {
    if (billingBusy != null) return;
    billingBusy = skoolId;
    billingError = null;
    try {
      const { url } = await startCheckout(skoolId);
      window.location.href = url; // hand off to Stripe's hosted checkout
    } catch (err: unknown) {
      billingError = billingMsg(err, 'Could not start checkout.');
      billingBusy = null; // on success we've already navigated away
    }
  }

  async function manageBilling(): Promise<void> {
    if (portalBusy) return;
    portalBusy = true;
    billingError = null;
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (err: unknown) {
      billingError =
        err instanceof ApiError && err.status === 409
          ? 'No billing set up yet — upgrade a community first.'
          : billingMsg(err, 'Could not open the billing portal.');
      portalBusy = false;
    }
  }

  /**
   * The cold load — ONE round trip normally, TWO when returning from Stripe
   * (doc 02 W1 + W10).
   *
   * What it replaces on the checkout return is five sequential round trips:
   * `POST /admin/billing/sync` → `GET /admin/me` → `GET /admin/communities` →
   * `GET /admin/communities` *again, forced* → `{moderators, voices}`. Two of those
   * were the same endpoint back to back.
   *
   * `POST /v1/me/billing/sync` answers with the whole `/v1/me` body, so the sync IS
   * the bootstrap — the account it returns is what the onboarding guard reads, which is
   * why the sync still has to come first on that path. The page's own two requests go out beside it either way.
   *
   * THE SECOND ROUND TRIP IS ONE REQUEST TOO MANY AND IT IS A CLIENT GAP, NOT A ROUTE
   * ONE: `@api/v1/me` can force a fresh bootstrap but cannot be handed one, so the
   * community list is seeded by re-fetching `/v1/me` through `refreshCommunities()`
   * rather than by adopting the body `sync` already returned. An `adoptBootstrap(body)`
   * there takes this path to the single round trip doc 04 §4.7 describes.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    const cameFromCheckout = consumeBillingParam();
    let pageErr: unknown = null;
    // Fired before anything is awaited: the team and the voices do not depend on the
    // account, on the community list, or on the sync.
    const pending = withCommunity(async (skoolId) => {
      const [team, voicePage] = await Promise.all([
        fetchStaff(skoolId),
        fetchStaffVoices(skoolId),
      ]);
      return { skoolId, staff: team.items, voices: voicePage.items };
    }).catch((err: unknown) => {
      pageErr = err;
      return null;
    });
    try {
      // Best-effort: a 503 here means Stripe isn't configured, which must not stop the
      // page rendering. The webhook is the durable backstop either way.
      const synced = cameFromCheckout
        ? await syncBilling(optimisticSkoolId()).catch(() => null)
        : null;
      if (synced) await refreshCommunities().catch(() => {});
      me = asAdminUser(synced ?? (await ensureBootstrap()));
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      await ensureCommunities();
      if (community.selectedId == null) return;
      const loaded = await pending;
      if (loaded) {
        staff = loaded.staff;
        voices = loaded.voices;
        watch.seen(loaded.skoolId);
      } else if (pageErr) {
        throw pageErr;
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }
  void init();

  // Reload on a community switch. The first community is already on screen from
  // `init`, so it is skipped (`loading` is true only while `init` is in flight).
  const watch = onCommunityChange(
    (id) => {
      q = '';
      results = [];
      expandedVoice = new Set();
      activeFacet = {};
      void loadStaff(id);
      void loadVoices(id);
    },
    { gate: () => !loading },
  );

  async function loadStaff(id: number): Promise<void> {
    staffLoading = true;
    try {
      staff = (await fetchStaff(id)).items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      staffLoading = false;
    }
  }

  async function loadVoices(id: number): Promise<void> {
    voicesLoading = true;
    try {
      voices = (await fetchStaffVoices(id)).items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      voicesLoading = false;
    }
  }

  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function onSearchInput(e: Event): void {
    q = (e.target as HTMLInputElement).value;
    clearTimeout(searchTimer);
    const id = community.selectedId;
    if (id == null) return;
    if (!q.trim()) {
      results = [];
      searching = false;
      return;
    }
    searching = true;
    searchTimer = setTimeout(() => void runSearch(id), 250);
  }

  async function runSearch(id: number): Promise<void> {
    const term = q.trim();
    if (!term) return;
    searching = true;
    try {
      results = (await searchMembers(id, term, 20)).items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      searching = false;
    }
  }

  function isStaff(role: string | null | undefined): boolean {
    return role === 'owner' || role === 'admin' || role === 'moderator';
  }

  /**
   * Assign a role, and patch the team from the SERVER'S answer.
   *
   * `POST /admin/members/set-role` returned `{status: "ok"}`, so the new chip had to
   * be reconstructed here out of whichever row the click came from. The v1 PATCH
   * returns the member in the same representation the list uses (doc 04 §4.6), so the
   * row that goes into `staff` is the row the server just wrote.
   */
  async function assign(source: StaffMember, role: MemberRoleValue): Promise<void> {
    const id = community.selectedId;
    if (id == null) return;
    const mid = source.member_id;
    busy = new Set(busy).add(mid);
    try {
      const { member } = await setMemberRole(id, mid, role);
      results = results.map((r) => (r.member_id === mid ? { ...r, role } : r));
      if (isStaff(role)) {
        const row: StaffMember = {
          member_id: member.member_id,
          external_id: member.external_id,
          handle: member.handle,
          name: member.name,
          picture_profile: member.picture_profile,
          role: member.role ?? role,
        };
        staff = staff.some((m) => m.member_id === mid)
          ? staff.map((m) => (m.member_id === mid ? row : m))
          : [...staff, row];
        staff = [...staff].sort(
          (a, b) =>
            (a.role ?? '').localeCompare(b.role ?? '') ||
            (a.handle ?? '').localeCompare(b.handle ?? ''),
        );
      } else {
        staff = staff.filter((m) => m.member_id !== mid);
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      const n = new Set(busy);
      n.delete(mid);
      busy = n;
    }
  }
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- "Account", not the invented "Sideguide AI · Settings": Preferences is the
           sidebar's own bottom item, in the block that holds the signed-in email, the
           role and Sign out. That is the only place in the navigation it belongs to. -->
      <PageHeader section="Account" title="Preferences" />
      <section class="rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Your team</div>
        <h2 class="mt-1 font-display text-xl font-semibold">Owners, admins & moderators</h2>
        <p class="mt-1.5 max-w-xl text-sm text-base-content/72">
          Skool's owners, admins, and moderators are synced automatically. Add anyone else who helps
          run the community — they're left out of your engagement insights and feed the moderation
          inbox.
        </p>

        <!-- Current staff -->
        <div class="mt-5">
          <div class="eyebrow mb-2 text-[0.6rem]">Team{staff.length ? ` · ${staff.length}` : ''}</div>
          {#if staffLoading}
            <StateBlock state="loading" compact />
          {:else if staff.length === 0}
            <p class="rounded-field border hair bg-base-100/40 px-3 py-3 text-sm text-base-content/68">
              No staff yet. They'll appear after the next sync, or add someone below.
            </p>
          {:else}
            <div class="flex flex-wrap gap-2">
              {#each staff as m (m.member_id)}
                <span class="flex items-center gap-2 rounded-full border hair bg-base-100/60 py-1 pr-1 pl-1.5">
                  <Avatar src={m.picture_profile} name={m.name} size={22} />
                  <span class="redact text-sm font-medium">{m.name}</span>
                  <span class="rounded bg-primary/10 px-1.5 py-px font-mono text-[0.6rem] uppercase tracking-wide text-primary/90">{LABEL[m.role ?? 'moderator']}</span>
                  <button
                    class="btn btn-ghost btn-xs btn-circle text-base-content/68 hover:text-error"
                    onclick={() => assign(m, 'member')}
                    disabled={busy.has(m.member_id)}
                    title="Remove from team"
                    aria-label={`Remove ${m.name} from the team`}
                  >
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                </span>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Add / assign role -->
        <div class="mt-6">
          <label class="eyebrow mb-2 block text-[0.6rem]" for="team-search">Add a team member</label>
          <input
            id="team-search"
            type="search"
            value={q}
            oninput={onSearchInput}
            placeholder="Search members by name or @handle"
            class="input input-bordered input-sm w-full bg-base-100"
            autocomplete="off"
          />

          {#if q.trim()}
            <div class="mt-2 overflow-hidden rounded-field border hair">
              {#if searching}
                <StateBlock state="loading" compact />
              {:else if results.length === 0}
                <p class="px-3 py-4 text-sm text-base-content/68">No members match "{q.trim()}".</p>
              {:else}
                <ul class="divide-y divide-base-content/5">
                  {#each results as r (r.member_id)}
                    {@const current = (roleOf.get(r.member_id) ?? r.role ?? 'member') as string}
                    <li class="flex items-center gap-3 bg-base-100/40 px-3 py-2">
                      <Avatar src={r.picture_profile} name={r.name} size={30} />
                      <div class="min-w-0 flex-1">
                        <div class="redact truncate text-sm font-medium">{r.name}</div>
                        <div class="redact font-mono text-[0.65rem] text-base-content/65">@{r.handle} · {r.stats?.activity ?? 0} activity</div>
                      </div>
                      <select
                        class="select select-bordered select-xs bg-base-100"
                        value={current}
                        disabled={busy.has(r.member_id)}
                        onchange={(e) => assign(r, (e.target as HTMLSelectElement).value as MemberRoleValue)}
                        aria-label={`Role for ${r.name}`}
                      >
                        {#each ROLES as role (role)}
                          <option value={role}>{LABEL[role]}</option>
                        {/each}
                      </select>
                    </li>
                  {/each}
                </ul>
              {/if}
            </div>
          {/if}
        </div>
      </section>

      <!-- AI Voices -->
      <section class="mt-6 rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Sideguide AI · Voices</div>
        <h2 class="mt-1 font-display text-xl font-semibold">AI Voices</h2>
        <p class="mt-1.5 max-w-xl text-sm text-base-content/72">
          For each team member we learn a writing-voice profile from their recent posts and
          comments, so suggested replies in the moderation inbox can sound like them. The owner's
          voice is the default. Profiles refresh automatically as they post more.
        </p>

        <div class="mt-5">
          <div class="eyebrow mb-2 text-[0.6rem]">Voices{withVoice.length ? ` · ${withVoice.length}` : ''}</div>
          {#if voicesLoading}
            <StateBlock state="loading" compact />
          {:else if voices.length === 0}
            <p class="rounded-field border hair bg-base-100/40 px-3 py-3 text-sm text-base-content/68">
              No team members yet — add owners, admins, or moderators above and their voices appear here.
            </p>
          {:else}
            <ul class="flex flex-col gap-2">
              {#each voices as v (v.member_id)}
                {@const open = expandedVoice.has(v.member_id)}
                <li class="overflow-hidden rounded-field border hair bg-base-100/50">
                  <div class="flex items-center gap-3 px-3 py-2.5">
                    <Avatar src={v.picture_profile} name={v.name ?? ''} size={30} memberId={v.member_id} />
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-x-2">
                        <span class="redact truncate text-sm font-medium">{v.name}</span>
                        <span class="rounded bg-primary/10 px-1.5 py-px font-mono text-[0.6rem] uppercase tracking-wide text-primary/90">{LABEL[v.role ?? 'moderator']}</span>
                      </div>
                      <div class="redact font-mono text-[0.65rem] text-base-content/65">
                        @{v.handle}{#if v.profile_text && v.source_count} · learned from {v.source_count} posts &amp; comments{/if}
                      </div>
                    </div>
                    {#if v.profile_text}
                      <button
                        class="btn btn-ghost btn-xs gap-1 text-base-content/72"
                        onclick={() => toggleVoice(v.member_id)}
                        aria-expanded={open}
                      >
                        {open ? 'Hide' : 'View voice'}
                        <svg class="h-3.5 w-3.5 transition-transform {open ? 'rotate-90' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                      </button>
                    {:else}
                      <span class="shrink-0 text-xs text-base-content/62">No voice yet</span>
                    {/if}
                  </div>
                  {#if v.profile_text}
                    {@const facet = activeFacet[v.member_id] ?? 'general'}
                    <div class="grid transition-[grid-template-rows] duration-200 ease-out {open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}">
                      <div class="overflow-hidden">
                        {#if v.profiles}
                          <div class="border-t hair px-3 pt-3">
                            <div role="tablist" class="tabs tabs-box tabs-xs bg-base-200/60 p-1">
                              {#each FACETS as f (f.key)}
                                <button
                                  role="tab"
                                  class="tab {facet === f.key ? 'tab-active' : ''}"
                                  onclick={() => (activeFacet = { ...activeFacet, [v.member_id]: f.key })}
                                >{f.label}</button>
                              {/each}
                            </div>
                          </div>
                          {#if facetText(v, facet)}
                            <p class="whitespace-pre-wrap px-3 py-3 text-sm leading-relaxed text-base-content/80">{facetText(v, facet)}</p>
                          {:else}
                            <p class="px-3 py-3 text-sm italic text-base-content/62">Not enough examples yet for this approach.</p>
                          {/if}
                        {:else}
                          <p class="whitespace-pre-wrap border-t hair px-3 py-3 text-sm leading-relaxed text-base-content/80">{v.profile_text}</p>
                        {/if}
                      </div>
                    </div>
                  {/if}
                </li>
              {/each}
            </ul>
            <p class="mt-2 text-xs text-base-content/62">
              A team member needs at least a handful of recent posts or comments before we can model their voice.
            </p>
          {/if}
        </div>
      </section>

      <!-- Billing -->
      <section class="mt-6 rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="eyebrow">Sideguide AI · Billing</div>
        <h2 class="mt-1 font-display text-xl font-semibold">Plans & billing</h2>
        <p class="mt-1.5 max-w-xl text-sm text-base-content/72">
          Billing is per community. Communities under 100 members stay free; larger ones run on a
          30-day trial, then Pro. Upgrade or cancel any of them here.
        </p>

        <!-- Return-from-checkout notes -->
        {#if billingSuccess}
          <div class="mt-4 flex items-start gap-2 rounded-field border border-success/30 bg-success/10 px-3 py-2.5 text-sm text-success">
            <span class="flex-1">Payment set up — your subscription is active. It can take a few seconds to reflect here.</span>
            <button class="shrink-0 text-success/70 hover:text-success" onclick={() => (billingSuccess = false)} aria-label="Dismiss">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        {/if}
        {#if billingCancelled}
          <div class="mt-4 flex items-start gap-2 rounded-field border hair bg-base-100/60 px-3 py-2.5 text-sm text-base-content/70">
            <span class="flex-1">Checkout cancelled — no changes were made.</span>
            <button class="shrink-0 text-base-content/50 hover:text-base-content/80" onclick={() => (billingCancelled = false)} aria-label="Dismiss">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        {/if}
        {#if billingError}
          <div class="mt-4 flex items-start gap-2 rounded-field border border-error/30 bg-error/10 px-3 py-2.5 text-sm text-error">
            <span class="flex-1">{billingError}</span>
            <button class="shrink-0 text-error/70 hover:text-error" onclick={() => (billingError = null)} aria-label="Dismiss">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        {/if}

        <div class="mt-5">
          <div class="eyebrow mb-2 text-[0.6rem]">Communities{community.list.length ? ` · ${community.list.length}` : ''}</div>
          {#if community.list.length === 0}
            <p class="rounded-field border hair bg-base-100/40 px-3 py-3 text-sm text-base-content/68">
              No communities on this account yet.
            </p>
          {:else}
            <ul class="divide-y divide-base-content/5 overflow-hidden rounded-field border hair">
              {#each community.list as c (c.id)}
                <li class="flex items-center gap-3 bg-base-100/40 px-3 py-2.5">
                  <span class="grid h-7 w-7 shrink-0 place-items-center rounded bg-primary/12 font-mono text-[0.7rem] font-bold text-primary">
                    <span class="redact">{c.name[0]?.toUpperCase() ?? '?'}</span>
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="redact truncate text-sm font-medium">{c.name}</div>
                    <div class="mt-0.5"><PlanBadge status={planOf(c)} trialEndsAt={c.trial_ends_at} /></div>
                  </div>
                  {#if canUpgrade(c)}
                    <button
                      class="btn btn-primary btn-xs gap-1"
                      onclick={() => upgrade(c.id)}
                      disabled={billingBusy === c.id}
                    >
                      {#if billingBusy === c.id}<span class="loading loading-spinner loading-xs"></span>{/if}
                      Upgrade
                    </button>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </div>

        <div class="mt-4 flex flex-wrap items-center gap-3">
          <button class="btn btn-sm btn-outline gap-2" onclick={manageBilling} disabled={portalBusy}>
            {#if portalBusy}<span class="loading loading-spinner loading-xs"></span>{/if}
            Manage billing
          </button>
          <span class="text-xs text-base-content/62">Update your card or cancel anytime — opens Stripe.</span>
        </div>
      </section>

      <!-- Affiliate program — links to the dedicated page under Preferences. -->
      <section class="mt-6 rounded-box border hair bg-base-100 p-5 sm:p-6">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="min-w-0">
            <div class="eyebrow">Sideguide AI · Affiliates</div>
            <h2 class="mt-1 font-display text-xl font-semibold">Affiliate program</h2>
            <p class="mt-1.5 max-w-xl text-sm text-base-content/72">
              Earn 30% recurring for every community you refer — for as long as they stay.
            </p>
          </div>
          <button
            class="btn btn-sm btn-outline shrink-0 gap-1"
            onclick={() => navigate('/admin/preferences/affiliates')}
          >
            Open affiliate program
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
          </button>
        </div>
      </section>

      <p class="mt-4 text-center text-xs text-base-content/62">More preferences coming soon.</p>
    </PageContainer>
  {/if}
</AdminShell>
